import { useHandler } from "@redotech/react-util/hook";
import { AllCookies, getCookie } from "@redotech/redo-web/utils/cookies";
import { ANONYMOUS_SHOPPER_ID_KEY } from "@redotech/redo-web/utils/shared-conf";
import { getSessionHazardProbabilities } from "@redotech/shopify-sdk/rpc/schema/checkout-optimization/get-session-hazard-probabilities";
import {
  Context,
  createContext,
  memo,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import * as z from "zod/v4";
import { RedoShopifyRpcClientContext } from "../../client/redo-shopify-rpc-client";
import { log } from "../../monitoring/log";

const UPDATE_INTERVAL_MS = 15_000;
const STALE_PAGE_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes
const SESSION_STORAGE_KEY = "redo-checkout-probability-state";

interface CheckoutProbabilityContextValue {
  cumulativeConversionProbability: number | null;
  instantaneousHazardProbability: number | null;
  previousCumulativeConversionProbability: number | null;
  previousInstantaneousHazardProbability: number | null;
}

const checkoutProbabilityStorageSchema = z.object({
  cumulativeConversionProbability: z.number().nullable(),
  instantaneousHazardProbability: z.number().nullable(),
});
type CheckoutProbabilityStorage = z.infer<
  typeof checkoutProbabilityStorageSchema
>;

export const CheckoutProbabilityContext: Context<
  CheckoutProbabilityContextValue | undefined
> = createContext<CheckoutProbabilityContextValue | undefined>(undefined);
CheckoutProbabilityContext.displayName = "CheckoutProbabilityContext";

// Helper functions for SessionStorage with Zod validation
const loadFromSessionStorage = (): CheckoutProbabilityStorage | null => {
  try {
    const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!stored) {
      return null;
    }

    const parsed = JSON.parse(stored);
    const result = checkoutProbabilityStorageSchema.safeParse(parsed);

    if (result.success) {
      return result.data;
    } else {
      console.warn(
        "Invalid checkout probability data in SessionStorage:",
        result.error,
      );
      // Clear invalid data
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
      return null;
    }
  } catch (error) {
    console.warn(
      "Failed to load checkout probability from SessionStorage:",
      error,
    );
    return null;
  }
};

const saveToSessionStorage = (storage: CheckoutProbabilityStorage): void => {
  try {
    // Validate before saving
    const validated = checkoutProbabilityStorageSchema.parse(storage);
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(validated));
  } catch (error) {
    console.warn(
      "Failed to save checkout probability to SessionStorage:",
      error,
    );
  }
};

const getInitialState = (): CheckoutProbabilityContextValue => {
  const stored = loadFromSessionStorage();
  return {
    cumulativeConversionProbability: null,
    instantaneousHazardProbability: null,
    // Use stored values as previous values if they exist
    previousCumulativeConversionProbability:
      stored?.cumulativeConversionProbability || null,
    previousInstantaneousHazardProbability:
      stored?.instantaneousHazardProbability || null,
  };
};

export const CheckoutProbabilityContextProvider = memo(
  function CheckoutProbabilityContextProvider({
    children,
  }: {
    children: ReactNode | ReactNode[];
  }) {
    const shopifyRpcClient = useContext(RedoShopifyRpcClientContext);

    const [state, setState] =
      useState<CheckoutProbabilityContextValue>(getInitialState);

    const intervalRef = useRef<NodeJS.Timeout>();
    const pageLoadTimeRef = useRef<number>(Date.now());

    const updateCheckoutProbability = useHandler(async () => {
      try {
        // Check if page has been stale for more than 10 minutes
        const timeSincePageLoad = Date.now() - pageLoadTimeRef.current;
        if (timeSincePageLoad > STALE_PAGE_TIMEOUT_MS) {
          log(
            `Page has been stale for ${Math.round(timeSincePageLoad / 1000 / 60)} minutes, skipping checkout probability update`,
          );
          return;
        }

        const shopifySessionId = getCookie(AllCookies.ShopifySessionId);
        if (!shopifySessionId) {
          log(
            "Shopify session ID not found, skipping checkout probability update",
          );
          return;
        }

        const anonymousShopperId = localStorage.getItem(
          ANONYMOUS_SHOPPER_ID_KEY,
        );
        if (!anonymousShopperId) {
          log(
            "Anonymous shopper ID not found, skipping checkout probability update",
          );
          return;
        }

        if (!shopifyRpcClient) {
          console.warn("Shopify RPC client not available");
          return;
        }

        const storeUrl =
          globalThis.redoStorefront?.storeUrl ?? globalThis.Shopify?.shop;
        if (!storeUrl) {
          console.warn("Store URL not available");
          return;
        }

        // Get the current checkout probability from RPC endpoint
        const result = await shopifyRpcClient(getSessionHazardProbabilities, {
          sessionId: shopifySessionId,
          anonymousShopperId,
        });

        if (!result) {
          console.warn("No result returned from RPC");
          return;
        }

        log(
          "result.instantaneousHazardProbability",
          result.instantaneousHazardProbability,
        );

        // Update all values in one setState call
        setState((prev) => {
          const newState: CheckoutProbabilityContextValue = {
            ...prev,
            previousCumulativeConversionProbability:
              prev.cumulativeConversionProbability,
            previousInstantaneousHazardProbability:
              prev.instantaneousHazardProbability,
            cumulativeConversionProbability:
              result.cumulativeConversionProbability,
            instantaneousHazardProbability:
              result.instantaneousHazardProbability,
          };

          // Save current values to SessionStorage for next page load
          saveToSessionStorage({
            cumulativeConversionProbability:
              newState.cumulativeConversionProbability,
            instantaneousHazardProbability:
              newState.instantaneousHazardProbability,
          });

          return newState;
        });
      } catch (err) {
        console.error("Error in checkout probability context:", err);
      }
    });

    useEffect(() => {
      // Initial update
      void updateCheckoutProbability();

      // Set up interval to update every 15 seconds
      intervalRef.current = setInterval(
        updateCheckoutProbability,
        UPDATE_INTERVAL_MS,
      );

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }, [updateCheckoutProbability]);

    return (
      <CheckoutProbabilityContext.Provider value={state}>
        {children}
      </CheckoutProbabilityContext.Provider>
    );
  },
);
